import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, collection, getDocs } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { useAuth } from "./AuthProvider";
import { useNavigate } from "react-router-dom";

interface AuthModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  defaultTab?: "login" | "signup";
}

const AuthModal = ({
  isOpen = true,
  onClose = () => {},
  defaultTab = "login",
}: AuthModalProps) => {
  const [activeTab, setActiveTab] = useState<"login" | "signup">(defaultTab);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  const [signupForm, setSignupForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    middleName: "",
    phone: "",
    dateOfBirth: "",
    address: "",
    gender: "",
    branch: "",
    ltoClientNumber: "",
    maritalStatus: "",
    licenseNumber: "",
  });

  // Instructor specific fields
  const [instructorFields, setInstructorFields] = useState({
    licenseNumber: "",
    yearsOfExperience: "",
  });

  // Admin specific fields
  const [adminFields, setAdminFields] = useState({
    adminCode: "",
  });

  const [branches, setBranches] = useState<any[]>([]);

  const { user, role, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab !== "signup") return;
    const fetchBranches = async () => {
      const snap = await getDocs(collection(db, "branches"));
      setBranches(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchBranches();
  }, [activeTab]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
      // No redirect here; handled by useEffect below
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to login. Please check your credentials.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Field validation
    if (!signupForm.firstName.trim()) {
      setError("First name is required.");
      return;
    }
    if (!signupForm.lastName.trim()) {
      setError("Last name is required.");
      return;
    }
    if (!signupForm.email.trim()) {
      setError("Email is required.");
      return;
    }
    if (!signupForm.phone.trim()) {
      setError("Phone number is required.");
      return;
    }
    if (!signupForm.dateOfBirth) {
      setError("Date of birth is required.");
      return;
    }
    if (!signupForm.gender) {
      setError("Gender is required.");
      return;
    }
    if (!signupForm.address.trim()) {
      setError("Address is required.");
      return;
    }
    if (!signupForm.password || signupForm.password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (signupForm.password !== signupForm.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!signupForm.branch) {
      setError("Branch selection is required.");
      return;
    }
    // Age validation
    const dob = new Date(signupForm.dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear() - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
    if (isNaN(age) || age < 17) {
      setError("You must be at least 17 years old to register.");
      return;
    }
    setIsLoading(true);
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupForm.email,
        signupForm.password,
      );
      // Create user document in Firestore (role always 'student')
      await setDoc(doc(db, "users", userCredential.user.uid), {
        email: signupForm.email,
        firstName: signupForm.firstName,
        lastName: signupForm.lastName,
        middleName: signupForm.middleName,
        phone: signupForm.phone,
        address: signupForm.address,
        dateOfBirth: signupForm.dateOfBirth,
        gender: signupForm.gender,
        branch: signupForm.branch,
        ltoClientNumber: signupForm.ltoClientNumber,
        maritalStatus: signupForm.maritalStatus,
        licenseNumber: signupForm.licenseNumber,
        role: "student",
        createdAt: new Date().toISOString(),
      });
      setActiveTab("login");
    } catch (err: any) {
      setError(err.message || "Failed to create account. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignupInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSignupForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleInstructorFieldChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setInstructorFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdminFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAdminFields((prev) => ({ ...prev, [name]: value }));
  };

  // Add this useEffect for redirect after login
  useEffect(() => {
    if (user && role && !loading) {
      if (role === "admin") navigate("/dashboard/admin");
      else if (role === "instructor") navigate("/dashboard/instructor");
      else if (role === "staff") navigate("/dashboard/staff");
      else navigate("/dashboard/student");
    }
  }, [user, role, loading, navigate]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] bg-background max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {activeTab === "login" ? "Welcome Back" : "Create Account"}
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as "login" | "signup")}
          className="w-full flex-1 flex flex-col"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="flex-1">
            <Card>
              <CardHeader>
                <CardDescription>
                  Enter your credentials to access your account
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleLoginSubmit}>
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="login-email"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={loginForm.email}
                      onChange={handleLoginInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Button
                        variant="link"
                        className="p-0 h-auto text-xs"
                        type="button"
                      >
                        Forgot password?
                      </Button>
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={loginForm.password}
                      onChange={handleLoginInputChange}
                      required
                    />
                  </div>
                </CardContent>

                <CardFooter>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="signup" className="flex-1 flex flex-col">
            <Card className="flex-1 flex flex-col">
              <CardHeader>
                <CardDescription>
                  Create a new account to get started
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleSignupSubmit} className="flex-1 flex flex-col">
                <CardContent className="space-y-4 flex-1 overflow-y-auto max-h-[60vh] pr-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={signupForm.firstName}
                        onChange={handleSignupInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={signupForm.lastName}
                        onChange={handleSignupInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="middleName">Middle Name</Label>
                    <Input
                      id="middleName"
                      name="middleName"
                      value={signupForm.middleName}
                      onChange={handleSignupInputChange}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="signup-email"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={signupForm.email}
                      onChange={handleSignupInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+63 XX XXX XXXX"
                      value={signupForm.phone}
                      onChange={handleSignupInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ltoClientNumber">LTO Client Number</Label>
                    <Input
                      id="ltoClientNumber"
                      name="ltoClientNumber"
                      placeholder="Enter LTO Client Number"
                      value={signupForm.ltoClientNumber}
                      onChange={handleSignupInputChange}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={signupForm.password}
                        onChange={handleSignupInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={signupForm.confirmPassword}
                        onChange={handleSignupInputChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Student Information */}
                  <div className="space-y-4 pt-2 border-t">
                    <CardTitle className="text-sm">Student Information</CardTitle>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="dateOfBirth">Date of Birth</Label>
                        <Input
                          id="dateOfBirth"
                          name="dateOfBirth"
                          type="date"
                          value={signupForm.dateOfBirth}
                          onChange={handleSignupInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gender">Gender</Label>
                        <select
                          id="gender"
                          name="gender"
                          value={signupForm.gender}
                          onChange={handleSignupInputChange}
                          required
                          className="w-full px-3 py-2 border border-input rounded-md bg-background"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="maritalStatus">Marital Status</Label>
                      <select
                        id="maritalStatus"
                        name="maritalStatus"
                        value={signupForm.maritalStatus}
                        onChange={handleSignupInputChange}
                        className="w-full px-3 py-2 border border-input rounded-md bg-background"
                      >
                        <option value="">Select Marital Status</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        name="address"
                        value={signupForm.address}
                        onChange={handleSignupInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="branch">Branch</Label>
                      <select
                        id="branch"
                        name="branch"
                        value={signupForm.branch}
                        onChange={handleSignupInputChange}
                        required
                        className="w-full px-3 py-2 border border-input rounded-md bg-background"
                      >
                        <option value="">Select Branch</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="licenseNumber">Student/Driver License Number</Label>
                      <Input
                        id="licenseNumber"
                        name="licenseNumber"
                        placeholder="Enter your license number (if applicable)"
                        value={signupForm.licenseNumber}
                        onChange={handleSignupInputChange}
                      />
                    </div>
                  </div>
                </CardContent>

                <CardFooter>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
